import { useFlowsDispatch } from '../../../../../../../common/src/utils/globalVariables';
import { useFlowsSession } from '../../../../../api/getFlowsSessionById';
import useAnswerForQuestion from '../../../../../hooks/useAnswerForQuestion';
import { useFlowsProgression } from '../../../../../hooks/useFlowsProgression';
import { useFlowsRouteParams } from '../../../../../hooks/useFlowsRouteParams';
import { replaceAnswerIndices } from '../../../../../store/redux/slices/flowsSlice';
import { FlowsQuestion } from '../../../../../types/FlowsTypes';
import getDuplicateQuestionsAnswers from '../utils/getDuplicateQuestionsAnswers';
import { useNextIndex } from './useAvailableIndices';
import useDuplicateGroupIds from './useDuplicateGroupIds';
import useUpdateItemAmount from './useUpdateItemAmount';

type AddTableItem = (currentIndex?: number) => void;

/**
 * This functions works as follows:
 * - Step 1: Replace all indices of the pending answers
 *   - Pending answers are answers that contain index -1
 * - Step 2: Update the amount question
 * - Step 3: Send changes to BE
 */
const useAddTableItem = (question: FlowsQuestion): AddTableItem => {
  const { sessionId } = useFlowsRouteParams();
  const { saveFields } = useFlowsProgression();
  const flowsDispatch = useFlowsDispatch();
  const updateItemAmount = useUpdateItemAmount(question);
  const duplicateGroupIds = useDuplicateGroupIds(question.id);
  const nextIndex = useNextIndex(question);
  const { refetch } = useFlowsSession(sessionId!);
  const getAnswerForQuestion = useAnswerForQuestion();

  const itemAmountAnswer =
    (getAnswerForQuestion(question.id)?.answer as number) || 0;

  return async (currentIndex?: number) => {
    if (question && currentIndex === -1) {
      // -- STEP 1: Replace indices of pending answers
      duplicateGroupIds.forEach((duplicateGroupId) => {
        flowsDispatch(
          replaceAnswerIndices({
            groupId: duplicateGroupId,
            forIndex: -1,
            toIndex: nextIndex,
          }),
        );
      });

      // -- STEP 2: Update amount question
      updateItemAmount(itemAmountAnswer ? itemAmountAnswer + 1 : 1);

      // -- STEP 3: Send changes to BE
      const answers = getDuplicateQuestionsAnswers(question?.id, nextIndex);
      await saveFields(answers);
    }
    refetch();
  };
};

export default useAddTableItem;
